import React, { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { PieChart, Pie, Tooltip as RechartsTooltip } from "recharts";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import { useMediaQuery } from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend } from "recharts";
import { firestore } from "firebaseConfig";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { useMaterialUIController } from "context";
import { formatCurrency } from "examples/FormatCurrency";

// Função para parsear valores monetários de diferentes formatos
const parseCurrencyValue = (valueString) => {
  if (!valueString) return 0;

  // Se já for número, retorna diretamente
  if (typeof valueString === "number") return valueString;

  // Remove qualquer caractere que não seja número, ponto ou vírgula
  let cleanedValue = valueString.toString().replace(/[^0-9,.]/g, "");

  // Detecta o formato com base nos separadores
  // Caso 1: "35.030,95" (formato BRL/EUR: ponto como milhar, vírgula como decimal)
  if (cleanedValue.includes(",") && cleanedValue.includes(".")) {
    cleanedValue = cleanedValue.replace(/\./g, ""); // Remove pontos (milhar)
    cleanedValue = cleanedValue.replace(",", "."); // Substitui vírgula por ponto
  }
  // Caso 2: "35030,95" (apenas vírgula, assume que é decimal)
  else if (cleanedValue.includes(",") && !cleanedValue.includes(".")) {
    cleanedValue = cleanedValue.replace(",", ".");
  }
  // Caso 3: "35030.95" (apenas ponto, assume que é decimal)
  else if (cleanedValue.includes(".") && !cleanedValue.includes(",")) {
    // Já está no formato correto para parseFloat
  }
  // Caso 4: "35,030,95" (múltiplas vírgulas, assume que são milhar e o último é decimal)
  else if (cleanedValue.includes(",")) {
    const parts = cleanedValue.split(",");
    const lastPart = parts.pop(); // Última parte (decimal)
    const integerPart = parts.join(""); // Junta as partes inteiras
    cleanedValue = `${integerPart}.${lastPart}`; // "35030.95"
  }

  // Converte para número e retorna 0 se inválido
  return parseFloat(cleanedValue) || 0;
};

const calcularRentabilidade = (valorAplicado, precoAtual) => {
  if (!valorAplicado || !precoAtual || valorAplicado <= 0) return 0;
  return ((precoAtual - valorAplicado) / valorAplicado) * 100;
};

function Investimentos() {
  const [controller] = useMaterialUIController();
  const { darkMode, currency } = controller;
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  const [selectedCategoriaAtivos, setSelectedCategoriaAtivos] = useState([]);
  const [openCategoriaModal, setOpenCategoriaModal] = useState(false);
  const isMobile = useMediaQuery("(max-width:768px)");
  const [patrimonioAtualizado, setPatrimonioAtualizado] = useState({
    totalPatrimonio: 0,
    categorias: {},
    historico: [],
  });

  const [investimentos, setInvestimentos] = useState([]);
  const [novoInvestimento, setNovoInvestimento] = useState({
    data: "",
    ativo: "",
    instituicao: "",
    quantidade: "",
    preco: "",
    valorAplicado: "",
    tipo: "",
    pais: "",
    operacao: "",
    categoria: "",
    indexador: "",
    taxa: "",
    vencimento: "",
    showMoreDetails: false,
    nota: "",
  });

  const calcularPatrimonio = (investimentosAtualizados = investimentos) => {
    let totalPatrimonio = 0;
    const categorias = {};
    const historicoAgrupado = {};

    investimentosAtualizados.forEach((investimento) => {
      const valor = parseCurrencyValue(investimento.valorAplicado);
      totalPatrimonio += valor;

      const categoria = investimento.categoria || "Outros";
      if (!categorias[categoria]) {
        categorias[categoria] = {
          ativos: [],
          total: 0,
        };
      }
      categorias[categoria].ativos.push({
        nome: investimento.ativo,
        valorInvestido: valor,
      });
      categorias[categoria].total += valor;

      const data = new Date(investimento.data || new Date());
      const mesAno = `${data.getFullYear()}-${data.getMonth() + 1}`;
      if (!historicoAgrupado[mesAno]) {
        historicoAgrupado[mesAno] = { mesAno, valor: 0 };
      }
      historicoAgrupado[mesAno].valor += valor;
    });

    const historicoOrdenado = Object.values(historicoAgrupado).sort((a, b) => {
      const [anoA, mesA] = a.mesAno.split("-").map(Number);
      const [anoB, mesB] = b.mesAno.split("-").map(Number);
      return new Date(anoA, mesA - 1) - new Date(anoB, mesB - 1);
    });

    const historicoFormatado = historicoOrdenado.map((item) => {
      const [ano, mes] = item.mesAno.split("-");
      const mesFormatado = new Date(ano, mes - 1).toLocaleString("pt-BR", {
        month: "short",
        year: "numeric",
      });
      return { data: mesFormatado, valor: item.valor };
    });

    setPatrimonioAtualizado({
      totalPatrimonio,
      categorias,
      historico: historicoFormatado,
    });
  };

  useEffect(() => {
    calcularPatrimonio(investimentos);
  }, [investimentos]);

  const handleCurrencyChange = (e, field) => {
    const inputValue = e.target.value.replace(/[^0-9]/g, "");
    if (!inputValue) {
      setNovoInvestimento((prevState) => ({
        ...prevState,
        [field]: "",
      }));
      return;
    }
    const numericValue = parseInt(inputValue, 10) / 100;
    setNovoInvestimento((prevState) => ({
      ...prevState,
      [field]: formatCurrency(numericValue, currency),
    }));
  };

  useEffect(() => {
    const calcularTotal = () => {
      const precoUnitario = parseCurrencyValue(novoInvestimento.preco);
      const quantidadeTotal = parseFloat(novoInvestimento.quantidade) || 0;
      const custosAdicionais =
        parseCurrencyValue(novoInvestimento.corretagem) +
        parseCurrencyValue(novoInvestimento.emolumentos) +
        parseCurrencyValue(novoInvestimento.impostos);
      const precoTotal = precoUnitario * quantidadeTotal;
      const total = precoTotal + custosAdicionais;
      return total;
    };
    const totalCalculado = calcularTotal();
    setNovoInvestimento((prevState) => ({
      ...prevState,
      total: formatCurrency(totalCalculado, currency),
    }));
  }, [
    novoInvestimento.preco,
    novoInvestimento.quantidade,
    novoInvestimento.corretagem,
    novoInvestimento.emolumentos,
    novoInvestimento.impostos,
    currency,
  ]);

  const [sugestoes, setSugestoes] = useState([]);
  const isRendaVariavel = novoInvestimento.tipo === "Renda Variável";

  const handleAtivoBlur = () => {
    let ativo = novoInvestimento.ativo.trim();
    if (novoInvestimento.tipo === "Renda Variável" && novoInvestimento.pais === "BR" && !ativo.includes(".")) {
      ativo += ".SA";
    }
    setNovoInvestimento((prevState) => ({ ...prevState, ativo }));
  };

  const investimentosFiltrados = investimentos.filter((investimento) => {
    const dataInvestimento = new Date(investimento.data);
    return dataInvestimento.getMonth() + 1 === selectedMonth && dataInvestimento.getFullYear() === selectedYear;
  });

  const [openPopup, setOpenPopup] = useState(false);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [openAddProventosPopup, setOpenAddProventosPopup] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [proventosValor, setProventosValor] = useState("");
  const [proventosData, setProventosData] = useState("");
  const [comparativoDados, setComparativoDados] = useState([]);
  const [openAlertPopup, setOpenAlertPopup] = useState(false);

  useEffect(() => {
    const fetchInvestimentos = async () => {
      const auth = getAuth();
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          try {
            const data = await getDocs(collection(firestore, "usuarios", user.uid, "investimentos"));
            const investimentosData = data.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id,
            }));
            setInvestimentos(investimentosData);

            let totalPatrimonio = 0;
            const historico = [];
            const categorias = {};

            investimentosData.forEach((investimento) => {
              const valor = parseCurrencyValue(investimento.valorAplicado);
              totalPatrimonio += valor;
              historico.push({
                data: investimento.data || "",
                valor,
              });
              const categoria = investimento.categoria || "Outros";
              if (!categorias[categoria]) {
                categorias[categoria] = {
                  ativos: [],
                  total: 0,
                };
              }
              categorias[categoria].ativos.push({
                nome: investimento.ativo,
                valorInvestido: valor,
              });
              categorias[categoria].total += valor;
            });

            setPatrimonioAtualizado({
              totalPatrimonio: totalPatrimonio || 0,
              historico,
              categorias,
            });
          } catch (err) {
            console.error("Erro ao buscar investimentos: ", err);
            setInvestimentos([]);
            setPatrimonioAtualizado({
              totalPatrimonio: 0,
              historico: [],
              categorias: {},
            });
          }
        }
      });
    };

    fetchInvestimentos();
  }, []);

  const resetNovoInvestimento = () => {
    setNovoInvestimento({
      data: "",
      ativo: "",
      instituicao: "",
      quantidade: "",
      preco: "",
      valorAplicado: "",
      tipo: "",
      pais: "",
      operacao: "",
      categoria: "",
      indexador: "",
      taxa: "",
      vencimento: "",
      showMoreDetails: false,
      nota: "",
    });
    setSelectedIndex(null);
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleAddInvestimento = async () => {
    try {
      const formattedInvestimento = {
        ativo: novoInvestimento.ativo,
        valorAplicado: formatCurrency(parseCurrencyValue(novoInvestimento.valorAplicado), currency),
        data: novoInvestimento.data || new Date().toISOString().split("T")[0],
        categoria: novoInvestimento.categoria || "",
        taxa: novoInvestimento.taxa || "",
        nota: novoInvestimento.nota || "",
      };

      const user = getAuth().currentUser;
      if (user) {
        if (selectedIndex !== null) {
          const investimentoDocRef = doc(firestore, "usuarios", user.uid, "investimentos", selectedIndex);
          await updateDoc(investimentoDocRef, formattedInvestimento);
          setInvestimentos((prev) =>
            prev.map((inv) => (inv.id === selectedIndex ? { ...formattedInvestimento, id: inv.id } : inv))
          );
        } else {
          const docRef = await addDoc(
            collection(firestore, "usuarios", user.uid, "investimentos"),
            formattedInvestimento
          );
          setInvestimentos((prev) => [...prev, { ...formattedInvestimento, id: docRef.id }]);
        }
      }

      calcularPatrimonio([...investimentos, formattedInvestimento]);
      resetNovoInvestimento();
      setOpenPopup(false);
    } catch (error) {
      console.error("Erro ao salvar investimento no Firestore:", error);
    }
  };

  const handleEditInvestimento = (id) => {
    const investimento = investimentos.find((inv) => inv.id === id);
    if (!investimento) return;

    setNovoInvestimento({
      ...investimento,
      showMoreDetails: false,
    });
    setSelectedIndex(id);
    setOpenPopup(true);
  };

  const handleUpdateInvestimento = async () => {
    if (selectedIndex === null) return;

    const investimento = {
      ...novoInvestimento,
      valorAplicado: formatCurrency(parseCurrencyValue(novoInvestimento.valorAplicado), currency),
      data: novoInvestimento.data || new Date().toISOString().split("T")[0],
      categoria: novoInvestimento.categoria || "",
      taxa: novoInvestimento.taxa || "",
      nota: novoInvestimento.nota || "",
    };

    try {
      const user = getAuth().currentUser;
      if (user) {
        const investimentoDocRef = doc(
          firestore,
          "usuarios",
          user.uid,
          "investimentos",
          investimentos[selectedIndex].id
        );

        await updateDoc(investimentoDocRef, investimento);

        setInvestimentos((prevInvestimentos) => {
          const updated = [...prevInvestimentos];
          updated[selectedIndex] = {
            ...investimento,
            id: investimentos[selectedIndex].id,
          };
          calcularPatrimonio(updated);
          return updated;
        });
      }
    } catch (err) {
      console.error("Erro ao atualizar investimento: ", err);
    }

    resetNovoInvestimento();
    setOpenPopup(false);
  };

  const handleDeleteInvestimento = (id) => {
    setSelectedIndex(id);
    setOpenConfirmDelete(true);
  };

  const confirmDeleteInvestimento = async () => {
    if (!selectedIndex) return;

    try {
      const user = getAuth().currentUser;
      if (user) {
        const investimentoDocRef = doc(firestore, "usuarios", user.uid, "investimentos", selectedIndex);
        await deleteDoc(investimentoDocRef);
        setInvestimentos((prevInvestimentos) => {
          const updated = prevInvestimentos.filter((investimento) => investimento.id !== selectedIndex);
          calcularPatrimonio(updated);
          return updated;
        });
      }
    } catch (err) {
      console.error("Erro ao excluir investimento: ", err);
    } finally {
      setOpenConfirmDelete(false);
      setSelectedIndex(null);
    }
  };

  const historicoAgrupado = Object.values(
    (patrimonioAtualizado.historico || []).reduce((acc, item) => {
      const dateObj = new Date(item.data);
      const mesAno = dateObj.toLocaleString("pt-BR", { month: "short", year: "numeric" });
      if (!acc[mesAno]) {
        acc[mesAno] = { data: mesAno, valor: 0 };
      }
      acc[mesAno].valor += item.valor;
      return acc;
    }, {})
  );

  const ordenarHistoricoAgrupado = (historico) => {
    const meses = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
    return historico.sort((a, b) => {
      const [mesA, anoA] = a.data.split(" ");
      const [mesB, anoB] = b.data.split(" ");
      const dataA = new Date(parseInt(anoA), meses.indexOf(mesA.toLowerCase()), 1);
      const dataB = new Date(parseInt(anoB), meses.indexOf(mesB.toLowerCase()), 1);
      return dataA - dataB;
    });
  };

  useEffect(() => {
    setPatrimonioAtualizado((prev) => ({
      ...prev,
      historico: ordenarHistoricoAgrupado(prev.historico || []),
    }));
  }, [patrimonioAtualizado.historico]);

  const renderPatrimonio = () => {
    if (!patrimonioAtualizado) {
      return (
        <MDBox mt={3}>
          <CircularProgress />
        </MDBox>
      );
    }

    const { totalPatrimonio, categorias } = patrimonioAtualizado;

    const dataGrafico = Object.entries(categorias).map(([categoria, dados]) => ({
      name: categoria,
      value: dados.ativos.reduce((sum, ativo) => sum + (ativo.valorInvestido || 0), 0),
    }));

    const historico = (patrimonioAtualizado.historico || []).sort((a, b) => {
      const dataA = new Date(a.data);
      const dataB = new Date(b.data);
      return dataA - dataB;
    });

    const mesAtual = historico[historico.length - 1]?.valor || 0;
    const mesAnterior = historico[historico.length - 2]?.valor || 0;
    const comparativo = mesAnterior > 0 ? `${(((mesAtual - mesAnterior) / mesAnterior) * 100).toFixed(2)}%` : "N/A";

    return (
      <MDBox mt={3}>
        <Card>
          <MDBox display="flex" flexDirection="column" alignItems="center" p={3}>
            <MDTypography variant="h6">Patrimônio Atual</MDTypography>
            <MDTypography variant="h4" color="info">
              {formatCurrency(totalPatrimonio, currency)}
            </MDTypography>

            <MDTypography variant="body2" color="success" mt={1}>
              Comparativo com o mês anterior: {comparativo}
            </MDTypography>

            <MDBox
              mt={4}
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {dataGrafico.length > 0 ? (
                <PieChart width={isMobile ? 300 : 700} height={isMobile ? 200 : 300} style={{ margin: "0 auto" }}>
                  <Pie
                    data={dataGrafico}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={isMobile ? 50 : 80}
                    outerRadius={isMobile ? 70 : 120}
                    fill="#8884d8"
                    paddingAngle={5}
                    label={({ name, value }) => {
                      const percentage = ((value / totalPatrimonio) * 100).toFixed(1);
                      return `${name}: ${percentage}%`;
                    }}
                    labelStyle={{
                      fontSize: isMobile ? "10px" : "12px",
                      fontWeight: "bold",
                    }}
                    labelLine={true}
                    isAnimationActive={true}
                  />
                  <RechartsTooltip formatter={(value) => formatCurrency(value, currency)} />
                </PieChart>
              ) : (
                <MDTypography variant="body2" color="error">
                  Nenhuma categoria encontrada.
                </MDTypography>
              )}
            </MDBox>

            <MDBox mt={4} style={{ maxWidth: "100%", overflowX: isMobile ? "scroll" : "visible" }}>
              <MDTypography variant="h6" align="center">
                Evolução do Patrimônio Mensal
              </MDTypography>
              <LineChart
                width={isMobile ? 300 : 600}
                height={isMobile ? 200 : 300}
                data={patrimonioAtualizado.historico}
                margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="data" tick={{ fontSize: isMobile ? 10 : 12 }} tickLine={false} />
                <YAxis
                  tickFormatter={(value) => formatCurrency(value, currency)}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <Tooltip formatter={(value) => formatCurrency(value, currency)} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="valor"
                  stroke="#8884d8"
                  name="Valor Investido"
                  animationDuration={800}
                  isAnimationActive={true}
                />
              </LineChart>
            </MDBox>

            <MDBox
              mt={4}
              alignSelf="flex-start"
              sx={{
                width: "100%",
                padding: "16px",
                backgroundColor: (theme) => (document.body.classList.contains("Mui-dark") ? "#2E2E2E" : "#FFFFFF"),
                borderRadius: "8px",
                boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
              }}
            >
              <MDTypography
                variant="h6"
                sx={{
                  marginBottom: "12px",
                  fontSize: "1rem",
                  color: document.body.classList.contains("Mui-dark") ? "#FFFFFF" : "#000000",
                }}
              >
                Alocação por Categoria
              </MDTypography>
              <MDBox
                component="ul"
                sx={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                {dataGrafico.map(({ name, value }) => (
                  <MDBox
                    component="li"
                    key={name}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px",
                      borderRadius: "4px",
                      backgroundColor: document.body.classList.contains("Mui-dark") ? "#3A3A3A" : "#F9F9F9",
                      boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        backgroundColor: document.body.classList.contains("Mui-dark") ? "#444444" : "#E0E0E0",
                      },
                    }}
                  >
                    <MDTypography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 300,
                        fontSize: "1rem",
                        color: document.body.classList.contains("Mui-dark") ? "#FFFFFF" : "#000000",
                        wordBreak: "break-word",
                        flex: 1,
                      }}
                    >
                      {name}
                    </MDTypography>
                    <MDTypography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 500,
                        color: document.body.classList.contains("Mui-dark") ? "#FFFFFF" : "#000000",
                        fontSize: "1rem",
                        whiteSpace: "nowrap",
                        marginLeft: "16px",
                      }}
                    >
                      {formatCurrency(value, currency)}
                    </MDTypography>
                  </MDBox>
                ))}
              </MDBox>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
    );
  };

  const renderCamposEspecificos = () => {
    if (["CDB", "LCI/LCA", "LC"].includes(novoInvestimento.categoria)) {
      return (
        <>
          <MDBox display="flex" gap={2} mb={2}>
            <FormControl fullWidth margin="normal" variant="outlined">
              <InputLabel id="forma-label">Forma</InputLabel>
              <Select
                labelId="forma-label"
                value={novoInvestimento.forma || ""}
                onChange={(e) =>
                  setNovoInvestimento({
                    ...novoInvestimento,
                    forma: e.target.value,
                  })
                }
                label="Forma"
              >
                <MenuItem value="Pós-Fixado">Pós-Fixado</MenuItem>
                <MenuItem value="Pré-Fixado">Pré-Fixado</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal" variant="outlined">
              <InputLabel id="indexador-label">Indexador</InputLabel>
              <Select
                labelId="indexador-label"
                value={novoInvestimento.indexador || ""}
                onChange={(e) =>
                  setNovoInvestimento({
                    ...novoInvestimento,
                    indexador: e.target.value,
                  })
                }
                label="Indexador"
              >
                <MenuItem value="CDI">CDI</MenuItem>
                <MenuItem value="IPCA">IPCA</MenuItem>
              </Select>
            </FormControl>
          </MDBox>
          <MDBox display="flex" gap={2} mb={2}>
            <TextField
              label="Taxa (%)"
              type="number"
              value={novoInvestimento.taxa || ""}
              onChange={(e) =>
                setNovoInvestimento({
                  ...novoInvestimento,
                  taxa: e.target.value,
                })
              }
              fullWidth
              margin="normal"
              variant="outlined"
            />
            <TextField
              label="Vencimento"
              type="date"
              value={novoInvestimento.vencimento || ""}
              onChange={(e) =>
                setNovoInvestimento({
                  ...novoInvestimento,
                  vencimento: e.target.value,
                })
              }
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
          </MDBox>
        </>
      );
    }
    return null;
  };

  const getCategorias = () => {
    return [
      "Ações",
      "FII",
      "CDB",
      "LCI/LCA",
      "Tesouro Direto",
      "BDRs",
      "Criptomoedas",
      "Stocks",
      "REITs",
      "ETFs",
      "Outros",
    ];
  };

  const renderInvestimentos = () => {
    return investimentosFiltrados.map((investimento, index) => {
      const valorAplicado = parseCurrencyValue(investimento.valorAplicado);

      return (
        <Grid item xs={12} md={4} key={investimento.id}>
          <Card
            sx={{
              borderRadius: "12px",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
              padding: "16px",
              position: "relative",
            }}
          >
            <MDBox display="flex" alignItems="center">
              <MDBox flexGrow={1}>
                <MDTypography variant="h6">{investimento.categoria || "Categoria não especificada"}</MDTypography>
                <MDTypography variant="body2" color="textSecondary">
                  Ativo: {investimento.ativo || "Não informado"}
                </MDTypography>
                <MDTypography variant="body2" color="textSecondary">
                  Data:{" "}
                  {new Date(investimento.data).toLocaleDateString("pt-BR", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })}
                </MDTypography>
                <MDTypography variant="body2" color="textSecondary">
                  Valor Aplicado: {formatCurrency(valorAplicado, currency)}
                </MDTypography>
                {investimento.indexador && (
                  <MDTypography variant="body2" color="textSecondary">
                    Indexador: {investimento.indexador}
                  </MDTypography>
                )}
                {investimento.taxa && (
                  <MDTypography variant="body2" color="textSecondary">
                    Taxa: {investimento.taxa}%
                  </MDTypography>
                )}
                {investimento.vencimento && (
                  <MDTypography variant="body2" color="textSecondary">
                    Vencimento:{" "}
                    {new Date(investimento.vencimento).toLocaleDateString("pt-BR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })}
                  </MDTypography>
                )}
              </MDBox>
              <MDBox>
                <IconButton onClick={() => handleEditInvestimento(investimento.id)} sx={{ color: "#FFA500" }}>
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => handleDeleteInvestimento(investimento.id)} sx={{ color: "#F44336" }}>
                  <DeleteIcon />
                </IconButton>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>
      );
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={1}>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          centered
          variant="fullWidth"
          sx={{
            p: 0.5,
            mx: "auto",
            width: { xs: "100%", sm: "auto" },
            borderRadius: 9999,
            background: darkMode ? "rgba(17,25,40,0.55)" : "rgba(255,255,255,0.65)",
            border: darkMode ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(30,136,229,0.15)",
            boxShadow: darkMode ? "0 6px 18px rgba(0,0,0,0.35)" : "0 6px 18px rgba(30,136,229,0.08)",
            backdropFilter: "saturate(180%) blur(8px)",
            "& .MuiTabs-indicator": { display: "none" },

            "& .MuiTab-root": {
              minHeight: 40,
              borderRadius: 9999,
              textTransform: "none",
              fontWeight: 700,
              fontSize: { xs: "0.9rem", md: "1rem" },
              px: { xs: 1.5, md: 2.5 },
              transition: "all .2s ease",
              color: darkMode ? "rgba(255,255,255,0.72)" : "#5E7FA3",

              "&:hover": {
                backgroundColor: darkMode ? "rgba(255,255,255,0.06)" : "rgba(221,241,255,0.7)",
              },

              "&.Mui-selected": {
                color: darkMode ? "#A6D9FF" : "#1565D8",
                background: darkMode
                  ? "linear-gradient(180deg, rgba(42,56,76,0.9) 0%, rgba(26,33,46,0.9) 100%)"
                  : "linear-gradient(180deg, #E6F5FF 0%, #F4FAFF 100%)",
                border: darkMode ? "1px solid rgba(166,217,255,0.35)" : "1px solid #BFE7FF",
                boxShadow: darkMode
                  ? "0 6px 14px rgba(0,0,0,0.40), inset 0 -2px 0 rgba(166,217,255,0.12)"
                  : "0 6px 14px rgba(21,101,216,0.12), inset 0 -2px 0 rgba(21,101,216,0.06)",
              },
            },
          }}
        >
          <Tab label="Cadastrar Investimento" />
          <Tab label="Meu Patrimônio" />
        </Tabs>

        <Divider />
        <MDBox mt={3}>
          {tabIndex === 0 ? (
            <>
              <MDBox mb={3} display="flex" justifyContent="center">
                <MDButton
                  startIcon={<AddIcon />}
                  onClick={() => {
                    resetNovoInvestimento();
                    setOpenPopup(true);
                  }}
                  sx={{
                    height: 36,
                    borderRadius: "10px",
                    px: 2,
                    backgroundColor: darkMode ? "#444" : "#fff",
                    color: darkMode ? "#fff" : "#1976d2",
                    border: darkMode ? "1px solid #666" : "1px solid #ddd",
                    textTransform: "none",
                    fontSize: "0.9rem",
                    "&:hover": { backgroundColor: darkMode ? "#555" : "#f0f0f0" },
                  }}
                >
                  Adicionar Investimento
                </MDButton>
              </MDBox>
              <Card>
                <MDBox p={3}>
                  <MDBox
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    flexDirection="row"
                    flexWrap="nowrap"
                    justifyContent="flex-end"
                    sx={{
                      width: "100%",
                      overflowX: "auto",
                      "&::-webkit-scrollbar": { display: "none" },
                      scrollbarWidth: "none",
                    }}
                  >
                    <FormControl variant="outlined" sx={{ minWidth: 70, flexShrink: 0 }}>
                      <Select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                        sx={{
                          height: "36px",
                          borderRadius: "20px",
                          backgroundColor: darkMode ? "#555" : "#f0f0f0",
                          color: darkMode ? "#fff" : "#333",
                          textAlign: "center",
                          "& .MuiSelect-icon": { color: darkMode ? "#fff" : "#333" },
                          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                        }}
                      >
                        {[2022, 2023, 2024, 2025, 2026, 2027, 2028, 2029, 2030].map((year) => (
                          <MenuItem key={year} value={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl variant="outlined" sx={{ minWidth: 90, flexShrink: 0 }}>
                      <Select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                        sx={{
                          height: "36px",
                          borderRadius: "20px",
                          backgroundColor: darkMode ? "#555" : "#f0f0f0",
                          color: darkMode ? "#fff" : "#333",
                          textAlign: "center",
                          "& .MuiSelect-icon": { color: darkMode ? "#fff" : "#333" },
                          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                        }}
                      >
                        {[...Array(12).keys()].map((month) => (
                          <MenuItem key={month + 1} value={month + 1}>
                            {new Date(0, month).toLocaleString("pt-BR", { month: "long" })}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </MDBox>
                  {investimentosFiltrados.length === 0 ? (
                    <MDBox
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      minHeight="50vh"
                      sx={{
                        background: "linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%)",
                        borderRadius: "16px",
                        padding: "32px",
                        textAlign: "center",
                      }}
                    >
                      <MDTypography variant="h5" color="dark" mb={2}>
                        Nenhum investimento cadastrado
                      </MDTypography>
                      <MDTypography variant="body1" color="textSecondary" mb={3}>
                        Adicione seu primeiro investimento e comece a acompanhar seu patrimônio!
                      </MDTypography>
                      <MDButton
                        variant="gradient"
                        color="info"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          resetNovoInvestimento();
                          setOpenPopup(true);
                        }}
                      >
                        Adicionar Primeiro Investimento
                      </MDButton>
                    </MDBox>
                  ) : (
                    <Grid container spacing={3} mt={4}>
                      {renderInvestimentos()}
                    </Grid>
                  )}
                </MDBox>
              </Card>
            </>
          ) : (
            renderPatrimonio()
          )}
        </MDBox>
      </MDBox>

      <Dialog
        open={openPopup}
        onClose={() => {
          resetNovoInvestimento();
          setOpenPopup(false);
        }}
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            boxShadow: theme.shadows[5],
          }),
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{selectedIndex !== null ? "Editar Investimento" : "Adicionar Investimento"}</span>
          <IconButton
            onClick={() => {
              resetNovoInvestimento();
              setOpenPopup(false);
            }}
            sx={{
              color: "grey.600",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal" variant="outlined">
            <Select
              value={novoInvestimento.categoria || ""}
              onChange={(e) =>
                setNovoInvestimento({
                  ...novoInvestimento,
                  categoria: e.target.value,
                })
              }
              displayEmpty
              inputProps={{ "aria-label": "Tipo de Investimento" }}
              renderValue={(selected) => selected || "Tipo de Investimento"}
            >
              <MenuItem value="" disabled>
                Selecione um tipo
              </MenuItem>
              {getCategorias().map((categoria) => (
                <MenuItem key={categoria} value={categoria}>
                  {categoria}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Nome do Investimento"
            value={novoInvestimento.ativo || ""}
            onChange={(e) =>
              setNovoInvestimento({
                ...novoInvestimento,
                ativo: e.target.value,
              })
            }
            fullWidth
            margin="normal"
            variant="outlined"
          />

          <TextField
            label={`Valor Investido (${currency})`}
            value={novoInvestimento.valorAplicado || ""}
            onChange={(e) => handleCurrencyChange(e, "valorAplicado")}
            fullWidth
            margin="normal"
            placeholder={formatCurrency(0, currency)}
            InputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
            variant="outlined"
          />

          <TextField
            label="Data"
            type="date"
            value={novoInvestimento.data || ""}
            onChange={(e) =>
              setNovoInvestimento({
                ...novoInvestimento,
                data: e.target.value,
              })
            }
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            variant="outlined"
          />

          <MDBox display="flex" justifyContent="flex-start" mt={2}>
            <MDButton
              variant="text"
              color="info"
              onClick={() =>
                setNovoInvestimento((prev) => ({
                  ...prev,
                  showMoreDetails: !prev.showMoreDetails,
                }))
              }
              startIcon={<AddIcon />}
            >
              Mais Detalhes
            </MDButton>
          </MDBox>

          {novoInvestimento.showMoreDetails && (
            <>
              <TextField
                label="Rendimento Esperado (%)"
                value={novoInvestimento.taxa || ""}
                onChange={(e) =>
                  setNovoInvestimento({
                    ...novoInvestimento,
                    taxa: e.target.value,
                  })
                }
                fullWidth
                margin="normal"
                type="number"
                variant="outlined"
              />
              <TextField
                label="Nota"
                value={novoInvestimento.nota || ""}
                onChange={(e) =>
                  setNovoInvestimento({
                    ...novoInvestimento,
                    nota: e.target.value,
                  })
                }
                fullWidth
                margin="normal"
                variant="outlined"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleAddInvestimento} color="info">
            Salvar
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openConfirmDelete}
        onClose={() => setOpenConfirmDelete(false)}
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            boxShadow: theme.shadows[5],
            borderRadius: "12px",
          }),
        }}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <MDTypography variant="body1">Tem certeza de que deseja excluir este investimento?</MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenConfirmDelete(false)} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={confirmDeleteInvestimento} color="error">
            Excluir
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAddProventosPopup}
        onClose={() => setOpenAddProventosPopup(false)}
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            boxShadow: theme.shadows[5],
          }),
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Adicionar Proventos/JCP</span>
          <IconButton
            onClick={() => setOpenAddProventosPopup(false)}
            sx={{
              color: "grey.600",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            label={`Valor (${currency})`}
            value={proventosValor}
            onChange={(e) => {
              const inputValue = e.target.value.replace(/[^0-9]/g, "");
              if (!inputValue) {
                setProventosValor("");
                return;
              }
              const numericValue = parseFloat(inputValue) / 100;
              setProventosValor(formatCurrency(numericValue, currency));
            }}
            fullWidth
            margin="normal"
            placeholder={formatCurrency(0, currency)}
            InputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
          />
          <TextField
            label="Data"
            type="date"
            value={proventosData}
            onChange={(e) => setProventosData(e.target.value)}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenAddProventosPopup(false)} color="info">
            Salvar
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAlertPopup}
        onClose={() => setOpenAlertPopup(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Atenção</DialogTitle>
        <DialogContent>
          <MDTypography variant="body1" id="alert-dialog-description">
            Por favor, selecione se é uma Compra ou Venda.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenAlertPopup(false)} color="info">
            OK
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Investimentos;
